import { Component, OnDestroy } from '@angular/core';
import { OotsRequest } from '../_model/oots-request';
import { Router } from '@angular/router';
import { OotsService } from '../_service/oots.service';
import { MatDialogConfig } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { AccessPointQueryParams } from '../_model/access-point-query-params';
import { AccessPointService } from '../_service/access-point.service';
import { Evidence } from '../_model/evidence';
import { COUNTRIES, CountryAsset } from '../contants';
import { TranslateService } from '@ngx-translate/core';
import { StateService } from '../_service/state.service';
import { catchError, EMPTY, Observable, Subscription, switchMap, tap } from 'rxjs';
import { ModalDialogService } from '../_service/modal-dialog.service';
import { ModalDialogComponent, ModalResult } from '../_common/modal-dialog/modal-dialog.component';
import { SessionTimerService } from '../_service/session-timer.service';
import { Action } from '../_model/action-enum';
import { BreadcrumbService } from '../_service/breadcrumb.service';
import { DocumentCounterService } from '../_service/document-counter.service';
import { Utils } from '../_common/utils';
import { EvidenceProvider } from '../_model/evidence-provider';
import { MultiLanguageStringPipe } from '../_model/multi-language-string';
import { JwtUtilsService } from '../_service/jwt-utils.service';
import { EvidenceStatus, OOTSRequestStatus, RequirementStatus } from '../_model/oots-request-status';
import { SessionService } from '../_service/session.service';
import { CountrySearchStatusEnum } from '../_model/country-search-status-enum';

const REDIRECT_BACK_URL = environment.redirect_back_url;

@Component({
  selector: 'app-locate-and-request-evidence',
  templateUrl: './locate-and-request-evidence.component.html',
  styleUrls: ['./locate-and-request-evidence.component.css']
})
export class LocateAndRequestEvidenceComponent implements OnDestroy {

  private eventsSubscription = new Subscription();
  ootsRequest: OotsRequest;
  requirementList: string[] = [];
  requestFound?: boolean;
  isModalVisible = false;
  previewSpaceUrl = "";
  evidenceUUID: string;
  countryCodeSelected: string;
  countryAsset: CountryAsset;
  procedureDescription: string;
  selectedEvidence: Evidence;
  action: string;
  dialogConfig: MatDialogConfig;
  step: number;
  evidencesValidity: string;
  receivedDocuments: number;
  totalDocuments: number;
  secCodesTimes = 0;
  storedRequirementsStatus: RequirementStatus[];
  isStep3Available: boolean;
  lang: string;
  redirecting = false;

  constructor(
    private ootsService: OotsService,
    private router: Router,
    private apService: AccessPointService,
    public translate: TranslateService,
    private stateService: StateService,
    private dialogService: ModalDialogService,
    private sessionTimerService: SessionTimerService,
    private breadcrumbService: BreadcrumbService,
    private documentCounterService: DocumentCounterService,
    private multiLanguagePipe: MultiLanguageStringPipe,
    private jwtUtilsService: JwtUtilsService,
    private sessionService: SessionService
  ) {

    this.ootsRequest = history.state;

    this.eventsSubscription.add(
      this.stateService.getLanguage.subscribe(lang => {
        if (this.lang != lang) this.lang = lang;
      }));

    this.eventsSubscription.add(
      this.stateService.ootsRequestObserver.subscribe((ootsRequest) => {
        this.ootsRequest = ootsRequest;
        this.loadOOTSRequest();
      }));

    this.eventsSubscription.add(
      this.stateService.actionObserver.subscribe((action) => {
        if (action === Action.CANCEL) this.openCancelProcessDialog();
        else this.saveOrShareOutOfSession(action);
      }));

    this.eventsSubscription.add(
      this.documentCounterService.receivedDocumentsObserver.subscribe(receivedDocuments => this.receivedDocuments = receivedDocuments)
    );

    this.eventsSubscription.add(
      this.documentCounterService.totalDocumentsObserver.subscribe(totalDocuments => this.totalDocuments = totalDocuments)
    );

    this.eventsSubscription.add(
      this.breadcrumbService.getStep.subscribe(step => this.step = step));

    this.eventsSubscription.add(
      this.stateService.requirementStatusObserver.subscribe(status => this.validateRequirementsStatus())
    );
  }

  ngOnDestroy(): void {
    this.eventsSubscription.unsubscribe();
  }

  loadOOTSRequest() {
    if (this.ootsRequest != null) {
      this.requestFound = true;
      switch (this.ootsRequest.procedureCode) {
        case 'T1': this.procedureDescription = "Applying for a tertiary education study financing, such as study grants and loans from a public body or institution"; break;
        case 'T2': this.procedureDescription = "Submitting an initial application for admission to public tertiary education institution"; break;
        case 'T3': this.procedureDescription = "Requesting academic recognition of diplomas, certificates or other proof of studies or courses"; break;
        case 'V1': this.procedureDescription = "Registering a change of address"; break;
        case 'R1': this.procedureDescription = "Requesting proof of registration of birth"; break;
      }
      if (this.ootsRequest.requirements) {
        const requirementIdList: string[] = [];
        this.ootsRequest.requirements.forEach(requirement => {
          this.requirementList.push(requirement.title);
          requirementIdList.push(requirement.id);
        });
        this.stateService.viewPdfMapInitialize(requirementIdList);
      }

      this.sessionService.getOOTSRequestStatus(this.ootsRequest.uuid).subscribe({
        next: (requestStatus) => {
          this.updateModelRequestStatus(requestStatus);
          this.handleRequestStatus();
          },
        error: (error) => {
          if (error.status == 404) this.manageNotFound();
        }
      });

      
    }
  }

  manageNotFound() {
    this.requestFound = false;
    const element = document.getElementById('notfound');
    if (element) element.classList.remove('hidden');
  }

  shareEvidences(action: string, email: string, phoneNumer: string) {
    const ootsRequestStatus = this.mapOOTSRequestStatusDto();
    this.sessionService.saveOOTSRequestStatus(ootsRequestStatus).pipe(
      switchMap(() => this.ootsService.getDocuments(this.ootsRequest.uuid, action, email, phoneNumer))).subscribe({
        next: response => {
          const result = JSON.parse(response);
          if (!result.success) return;

          const { returnMethod, returnUrl, ootsResponseXML } = result;
          const isInSession = this.ootsRequest.inSession;

          if (isInSession && returnMethod === 'POST') {
            this.doPostWithEvidences(returnUrl, ootsResponseXML);
          } else {
            this.doRedirect(returnUrl);
          }
        },
        error: error => {
          if (error.status === 404) this.manageNotFound();
        }
      })
  }

  mapOOTSRequestStatusDto(): OOTSRequestStatus {
    const result: RequirementStatus[] = [];

    this.ootsRequest.requirements.forEach(requirement => {
      const evidences: EvidenceStatus[] = requirement.evidences.map(evidence => {
        const evTitle = this.multiLanguagePipe.transform(evidence.evidenceTitle, this.lang) || evidence.evidenceTitle?.[0]?.value || "";

        return {
          evidenceUUID: evidence.evidenceUUID,
          countryCode: evidence.countryCode,
          evidenceIdentifier: evidence.evidenceIdentifier,
          status: evidence.status,
          evidenceTypeClassification: evidence.evidenceTypeClassification,
          evidenceTitle: evTitle,
          accessServiceIdentifier: evidence.accessServiceIdentifier,
          publisherIdentifier: evidence.publisherIdentifier,
          publisherName: evidence.publisherName
        } as EvidenceStatus;
      });

      const reqStatus = new RequirementStatus(requirement.id, requirement.status, evidences);
      result.push(reqStatus);
    });

    return new OOTSRequestStatus(this.ootsRequest.uuid, result);
  }


  saveOrShareOutOfSession(action: string) {
    this.action = action;
    document.getElementById("show-secCode-dialog")?.click();
  }

  finishAndNotify(email: string, phoneNumber: string) {
    this.shareEvidences(this.action, email, phoneNumber);
  }

  finishSession() {
    this.saveOrShareOutOfSession(Action.FINISH);
  }

  doRedirect(returnUrl: string) {
    window.location.href = returnUrl;
  }

  doPostWithEvidences(returnUrl: string, xml: string) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = returnUrl;
    form.enctype = "multipart/form-data";
    const hiddenField = document.createElement('input');
    hiddenField.type = 'hidden';
    hiddenField.name = 'data';
    hiddenField.value = xml;
    form.appendChild(hiddenField);
    document.body.appendChild(form);
    form.submit();
  }

  evidenceListener(evidence: Evidence) {
    evidence = Object.assign(new Evidence(), evidence);
    this.updateEvidenceData(evidence);
    this.selectedEvidence = evidence;
    if (evidence.isTimeout) document.getElementById("show-timeout-dialog")?.click();
    else if (evidence.status === 'ERROR') document.getElementById("show-error-dialog")?.click();
    else if (evidence.isPreview) {
      let delimiter = "?";
      if (evidence.previewUrl.indexOf("?") !== -1) delimiter = "&";
      const previewSpaceUrl = evidence.previewUrl + delimiter + "returnurl=" + encodeURIComponent(REDIRECT_BACK_URL + "?uuid=") + this.ootsRequest.uuid + "&returnmethod=GET";
      this.previewSpaceUrl = previewSpaceUrl;
      document.getElementById("show-preview-dialog")?.click();
    }
  }

  updateEvidenceData(evidence: Evidence) {
    this.ootsRequest.requirements.forEach(requirement => {
      if (requirement.id === evidence.requirementId && Array.isArray(requirement.evidences)) {
        const existing = requirement.evidences.find(ev => ev.evidenceIdentifier === evidence.evidenceIdentifier);
        if (existing) {
          Object.assign(existing, evidence);
        }
      }
    });
  }

  countryListener($event: any) {
    this.countryCodeSelected = $event;
    this.countryAsset = COUNTRIES[this.countryCodeSelected] ?? COUNTRIES['EU'];
    this.validateRequirementsStatus();
  }

  doSecondCall() {
    this.redirecting = true;
    const params = new AccessPointQueryParams();
    params.requestType = "REQUEST_2";
    params.ootsRequestId = this.ootsRequest.uuid;
    params.evidenceUUID = this.selectedEvidence.evidenceUUID;
    params.evidenceRequirementId = this.selectedEvidence.requirementId;
    params.countryCode = this.selectedEvidence.countryCode;
    params.accessServiceIdentifier = this.selectedEvidence.selectedProvider.accessServiceIdentifier;
    params.evidenceClassificationId = this.selectedEvidence.evidenceTypeClassification;
    params.evidenceIdentifier = this.selectedEvidence.evidenceIdentifier;
    params.requirementNumber = this.selectedEvidence.requirementNumber;
    this.apService.sendRequest(params).subscribe(
      {
        next: response => {
          const result = JSON.parse(response);
          if (result.success) {
            this.delay(3000); // wait 3 seconds
            this.redirecting = false;
            this.redirectToPreviewSpace();
          }
          else {
            this.selectedEvidence.errorMessage = result.message;
            this.openErrorDialog();
          }
        },
        error: (e) => {
          console.error(e);
        },
        complete: () => {
          console.log("complete");
        }
      });
  }

  redirectToPreviewSpace() {
    this.jwtUtilsService.saveRedirect('true');
    window.location.href = this.previewSpaceUrl;
  }

  openPreviewDialog() {
    this.countryAsset = COUNTRIES[this.selectedEvidence.countryCode] ?? COUNTRIES['EU'];
    const data = {
      title: this.translate.instant('LOCATE.PREVIEW_SPACE_MODAL'),
      message: this.translate.instant('LOCATE.PREVIEW_SPACE_MODAL_DESC', { document_name: this.multiLanguagePipe.transform(this.selectedEvidence.evidenceTitle, this.lang) ?? "", provider: this.selectedEvidence.selectedProvider.publisherName.toString() }),
      toAsset: this.countryAsset,
      type: 'preview'
    };

    this.dialogService.openDialog(ModalDialogComponent, data)
      .subscribe(result => {
        if (result?.action === 'accept') {
          this.doSecondCall();
        } else if (result?.action === 'cancel') {
          this.ootsService.cancelEvidence(this.selectedEvidence.evidenceUUID).subscribe(
            {
              next: () => {
                this.previewSpaceUrl = "";
                this.stateService.ootsRequestModelChanged(this.ootsRequest.uuid);

              },
              error: (e) => { console.error(e) },
            });
        }
      });
  }

  delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  openTimeoutDialog() {
    const data = {
      title: this.translate.instant('LOCATE.TIMEOUT_MODAL'),
      message: this.translate.instant('LOCATE.TIMEOUT_MODAL_DESC'),
      type: 'timeout'
    };
    this.dialogService.openDialog(ModalDialogComponent, data);
  }

  openErrorDialog() {
    const data = {
      title: this.translate.instant('LOCATE.ERROR_MODAL'),
      message: this.translate.instant('LOCATE.ERROR_MODAL_DESC_1') + this.selectedEvidence.errorMessage + this.translate.instant('LOCATE.ERROR_MODAL_DESC_2'),
      type: 'error'
    };
    this.dialogService.openErrorDialog(ModalDialogComponent, data);
  }

  openContactDialog() {
    const data = {
      title: this.translate.instant('LOCATE.CONTACT_INFORMATION_MODAL'),
      message: this.translate.instant('LOCATE.CONTACT_INFORMATION_MODAL_1'),
      type: 'contact'
    };
    this.dialogService.openDialog(ModalDialogComponent, data)
      .subscribe(result => {
        if (result?.action === 'accept') {
          this.finishAndNotify(result.email, result.phoneNumber);
        }
      });
  }

  openSecurityCodeDialog() {
    this.secCodesTimes = 0;
    const codeData = this.createDialogData('LOCATE.SECURITY_CODE_MODAL', 'LOCATE.SECURITY_CODE_MODAL_DESC', 'secCode');
    const confirmData = this.createDialogData('LOCATE.CONFIRM_SECURITY_CODE_MODAL', 'LOCATE.CONFIRM_SECURITY_CODE_MODAL_DESC', 'confirmCode');
    const saveSuccessData = this.createDialogData('LOCATE.SAVE_SUCCESS_MODAL', 'LOCATE.SAVE_SUCCESS_MODAL_DESC', 'saveSuccess');
    const saveErrorData = this.createDialogData('LOCATE.SAVE_ERROR_MODAL', 'LOCATE.SAVE_ERROR_MODAL_DESC', 'saveError');
    
    this.eventsSubscription.add(
      this.dialogService.dialogObserver.pipe(
        tap(() => this.dialogService.loading(true)),
        switchMap((result: ModalResult) => {
          const lang = window.localStorage.getItem('lang') ?? 'es';

          switch (result.type) {
            case 'secCode':
              return this.handleSecurityCode(result, lang, codeData, confirmData);

            case 'confirmCode':
              return this.handleConfirmCode(result, lang, confirmData, saveSuccessData, saveErrorData);

            case 'saveSuccess':
            case 'saveError':
              return this.handleFinalDialog();

            default:
              return EMPTY;
          }
        })
      ).subscribe()
    );
    this.dialogService.openDialog(ModalDialogComponent, codeData);
  }

  createDialogData(titleKey: string, msgKey: string, type: string, errorMessage: string = '') {
    return {
      title: this.translate.instant(titleKey),
      message: this.translate.instant(msgKey),
      type,
      errorMessage
    };
  }

  handleDialogResult(
    result: ModalResult,
    codeData: any,
    confirmData: any,
    saveSuccessData: any,
    saveErrorData: any
  ) {
    const lang = window.localStorage.getItem('lang') ?? 'es';

    const handlers: Record<string, () => Observable<any>> = {
      secCode: () => this.handleSecurityCode(result, lang, codeData, confirmData),
      confirmCode: () => this.handleConfirmCode(result, lang, confirmData, saveSuccessData, saveErrorData),
      saveSuccess: () => this.handleFinalDialog(),
      saveError: () => this.handleFinalDialog(),
    };

    return handlers[result.type]?.() ?? EMPTY;
  }

  handleSecurityCode(result: ModalResult, lang: string, codeData: any, confirmData: any) {
    if (!result.emailValid) {
      codeData.errorMessage = result.errorMessage;
      this.dialogService.setDialogData(codeData);
      this.dialogService.loading(false);
      return EMPTY;
    }

    return this.sessionService.getSecurityCode(this.ootsRequest.uuid, this.action, result.email, lang).pipe(
      tap(response => {
        const parsed = JSON.parse(response);
        this.dialogService.loading(false);
        if (parsed.success) this.dialogService.setDialogData(confirmData);
      }),
      catchError(() => {
        this.dialogService.loading(false);
        return EMPTY;
      })
    );
  }

  handleConfirmCode(
    result: ModalResult,
    lang: string,
    confirmData: any,
    saveSuccessData: any,
    saveErrorData: any
  ) {
    const ootsRequestStatus = this.mapOOTSRequestStatusDto();

    return this.sessionService.saveOOTSRequestStatus(ootsRequestStatus).pipe(
      switchMap(() => this.sessionService.validateSecurityCode(this.ootsRequest.uuid, result.code, lang)),
      tap(response => this.processConfirmResponse(response, confirmData, saveSuccessData, saveErrorData)),
      catchError(() => {
        this.dialogService.loading(false);
        this.dialogService.setDialogData(saveErrorData);
        return EMPTY;
      })
    );
  }

  handleFinalDialog() {
    this.dialogService.closeDialog();
    this.doRedirect(this.sessionTimerService.sessionData.returnUrl);
    return EMPTY;
  }

  processConfirmResponse(response: any, confirmData: any, saveSuccessData: any, saveErrorData: any) {
    this.dialogService.loading(false);
    const result = JSON.parse(response);

    if (result.success) {
      this.dialogService.setDialogData(saveSuccessData);
      return;
    }

    this.secCodesTimes++;
    if (this.secCodesTimes < 3) {
      confirmData.errorMessage = this.translate
        .instant('LOCATE.CONFIRM_SECURITY_CODE_ERROR_MSG')
        .replace('[X]', 3 - this.secCodesTimes);
      this.dialogService.setDialogData(confirmData);
    } else {
      this.dialogService.setDialogData(saveErrorData);
    }
  }

  openCancelProcessDialog() {
    const data = {
      title: this.translate.instant('LOCATE.CANCEL_PROCESS_MODAL'),
      message: this.translate.instant('LOCATE.CANCEL_PROCESS_MODAL_DESC'),
      type: 'cancel'
    };
    this.dialogService.openDialog(ModalDialogComponent, data).subscribe(result => {
      if (result?.action === 'accept') {
        this.doRedirect(this.sessionTimerService.sessionData.returnUrl);
      } else if (result?.action === 'cancel') {
        this.dialogService.closeDialog();
      }
    });
  }

  locateDocuments() {
    let count = 0;
    this.storedRequirementsStatus = [];
    this.ootsRequest.requirements.forEach(requirement => {
      const evidenceStatus: EvidenceStatus[] = [];
      requirement.evidences.forEach(evidence => {
        if (!Utils.isNullOrUndefined(evidence.providers))
          evidence.providers.forEach(provider => {
            if (provider.isSelected) {
              count++;
              const evStatus = new EvidenceStatus();
              evStatus.status = evidence.status;
              evStatus.evidenceUUID = evidence.evidenceUUID;
              evStatus.evidenceIdentifier = evidence.evidenceIdentifier;
              evStatus.evidenceTitle = this.multiLanguagePipe.transform(evidence.evidenceTitle, this.lang) ?? "";
              evStatus.accessServiceIdentifier = provider.accessServiceIdentifier;
              evStatus.publisherIdentifier = provider.publisherIdentifier;
              evStatus.publisherName = provider.publisherName;
              evStatus.countryCode = requirement.selectedCountryCode;
              evStatus.evidenceTypeClassification = evidence.evidenceTypeClassification;
              evidenceStatus.push(evStatus);
            }
          });
      });
      this.storedRequirementsStatus.push(new RequirementStatus(requirement.id, requirement.status, evidenceStatus));
    });
    this.sessionService.saveOOTSRequestStatus(new OOTSRequestStatus(this.ootsRequest.uuid, this.storedRequirementsStatus)).subscribe(
      {
        next: () => { this.updateStep(3) },
        error: (error) => {
          console.log(error);
        }
      });
  }

  updateStep(newStep: number) {
    if (this.step !== newStep) {
      this.breadcrumbService.setStep(newStep)
    }
  }

  mockSedeResponse() {
    this.router.navigate(['/sede'], { queryParams: { retrieveResponse: true, uuid: this.ootsRequest.uuid } });
  }

  handleRequestStatus() {

    if (!this.ootsRequest || !this.ootsRequest.requirements || this.ootsRequest.requirements.length === 0) {
      this.documentCounterService.setDocuments(0, 0);
      this.updateStep(2);
      return;
    }

    let total = 0;
    let received = 0;
    this.ootsRequest.requirements.forEach(req => {
      req.evidences?.forEach(ev => {
        total++;
        if (ev.status === 'RECEIVED') received++;
      });
    });

    this.documentCounterService.setDocuments(total, received);

    const allInReceivedOrPreview = this.ootsRequest.requirements.every(req =>
      req.status === 'DISCARDED' ||
      (
      req.evidences?.length > 0 &&
      req.evidences.every(ev => ev.status === 'RECEIVED' || ev.status === 'PREVIEW_AVAILABLE' || ev.providers !== null)
      )
    );

    if (allInReceivedOrPreview) this.updateStep(3);
    else this.updateStep(2);
  }

  updateModelRequestStatus(ootsRequestStatus: any) {
    if (!ootsRequestStatus || !ootsRequestStatus.requirements?.length) return;

    this.storedRequirementsStatus = ootsRequestStatus.requirements;
    let count = 0;

    this.ootsRequest.requirements.forEach((requirement, i) => {
      const storedReq = this.storedRequirementsStatus[i];
      if (!storedReq) return;
      if (storedReq.status === "DISCARDED") requirement.status = "DISCARDED";
      const storedEvidences = storedReq.evidences ?? [];

      if (requirement.evidences.length > 0) {
        requirement.evidences = requirement.evidences.map((ev) => {
          count++;
          const newEv = this.buildEvidenceFrom(ev);
          const matchedStoredEv = storedEvidences.find((s) => s.evidenceIdentifier === newEv.evidenceIdentifier);
          if (matchedStoredEv) this.mergeStoredEvidenceData(newEv, matchedStoredEv);
          return newEv;
        });
      }
      else {
        requirement.evidences = storedEvidences.map((storedEv) => {
          count++;
          return this.buildEvidenceFromStored(storedEv);
        });
      }
    });
  }

  buildEvidenceFrom(evidence: any): Evidence {
    const newEvidence = new Evidence();
    newEvidence.fillDataEvidence(evidence);
    return newEvidence;
  }

  mergeStoredEvidenceData(evidence: Evidence, storedEv: any): void {
    if (!evidence.evidenceTitle?.length) {
      evidence.evidenceTitle = [{ value: storedEv.evidenceTitle, lang: "ES" }];
    }
    evidence.evidenceTypeClassification = storedEv.evidenceTypeClassification;
    evidence.status = evidence.status === "RECEIVED" ? "RECEIVED" : storedEv.status;
    evidence.countryCode = storedEv.countryCode;

    const provider = new EvidenceProvider();
    provider.isSelected = true;
    provider.accessServiceIdentifier = evidence.accessServiceIdentifier ?? storedEv.accessServiceIdentifier;
    provider.publisherIdentifier = evidence.publisherIdentifier ?? storedEv.publisherIdentifier;
    provider.publisherName = evidence.publisherName ?? storedEv.publisherName;

    evidence.providers = [provider];
  }

  buildEvidenceFromStored(storedEv: any): Evidence {
    const evidence = new Evidence();
    evidence.evidenceTitle = [{ value: storedEv.evidenceTitle, lang: "ES" }];
    evidence.evidenceTypeClassification = storedEv.evidenceTypeClassification;
    evidence.status = storedEv.status;
    evidence.countryCode = storedEv.countryCode;
    evidence.evidenceIdentifier = storedEv.evidenceIdentifier;

    const provider = new EvidenceProvider();
    provider.isSelected = true;
    provider.accessServiceIdentifier = storedEv.accessServiceIdentifier;
    provider.publisherIdentifier = storedEv.publisherIdentifier;
    provider.publisherName = storedEv.publisherName;

    evidence.providers = [provider];
    return evidence;
  }

  validateRequirementsStatus() {
    const csError = this.ootsRequest.requirements.every(requirement =>
      requirement.countrySearchStatus === CountrySearchStatusEnum.NO_CS
    );
    if(csError){
      this.openGenericErrorDialog(this.translate.instant('LOCATE.ERROR_COMMON_SERVICES'));
    }
    else{
      this.isStep3Available = this.ootsRequest.requirements.every(requirement =>
      requirement.selectedCountryCode === "NONE" ||
      requirement.evidences.some(evidence =>
        // Caso 1: evidencia ya recibida
        evidence.status === "RECEIVED" ||
        // Caso 2: provider seleccionado y válido
        (!Utils.isNullOrUndefined(evidence.providers) &&
          evidence.providers.some(provider =>
            provider.isSelected && provider.accessServiceIdentifier !== null
          ))
        )
      );
    }
  }

  openGenericErrorDialog(errorText: string) {
    const data = {
      title: this.translate.instant('LOCATE.ERROR_MODAL'),
      message: errorText,
      type: 'error'
    };
    this.dialogService.openErrorDialog(ModalDialogComponent, data);
  }


  isStep4Available(): boolean {
    return this.totalDocuments === this.receivedDocuments;
  }

}